// src/utils/chatUtils.js
import * as chatService from '../services/chatService';
import * as offersService from '../services/offersService';
import { errorDev } from './logger';

// Re-export the main functions from chatService for backwards compatibility
export const findOrCreateChat = chatService.findOrCreateChat;
export const getChatMeta = chatService.getChatMeta;
export const markChatRead = chatService.markChatRead;
export const setTyping = chatService.setTyping;

// Export the new functions with the old names for compatibility
export const listenToMessages = chatService.listenMessages;
export const postMessage = chatService.sendMessage;
export const getLastMessagePreview = chatService.getLastMessagePreview;

// Keep the existing listenToInbox implementation but ensure it uses the new service
export const listenToInbox = (uid, cb) => {
  if (!uid) return () => {};

  const chatsMap = new Map();
  const offersMap = new Map();

  const normalizeTimestamp = (t) => {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (t?.seconds) return t.seconds;
    try { return new Date(t).getTime(); } catch (err) { errorDev("Operation failed:", err);  return 0; }
  };

  const emit = () => {
    const merged = new Map();

    // Copy chat docs first
    for (const [cid, ch] of chatsMap.entries()) {
      merged.set(cid, {
        id: cid,
        ...ch
      });
    }

    // Add offers-derived entries
    for (const o of offersMap.values()) {
      const chatId = o.chatId || chatService.convoId(o.buyerId, o.sellerId, o.productId);
      if (!chatId) continue;
      if (o.chatId && !chatsMap.has(chatId)) continue;
      const existing = merged.get(chatId);

      const offerUpdatedAt = o.createdAt ?? o.updatedAt ?? null;
      const offerEntry = {
        id: chatId,
        participants: existing?.participants || [String(o.buyerId), String(o.sellerId)].sort(),
        productId: existing?.productId || o.productId || null,
        lastMessage: `Offer: ${o.amount}`,
        lastMessageType: 'offer',
        lastOfferAmount: o.amount,
        updatedAt: existing?.updatedAt || offerUpdatedAt || o.createdAt || o.updatedAt || null,
        offers: (existing?.offers ? existing.offers.slice() : []).concat([o]),
      };

      if (existing) {
        merged.set(chatId, {
          ...existing,
          offers: (existing.offers || []).concat([o]),
          updatedAt: (normalizeTimestamp(offerEntry.updatedAt) > normalizeTimestamp(existing.updatedAt)) ? offerEntry.updatedAt : existing.updatedAt,
          lastOfferAmount: offerEntry.lastOfferAmount || existing.lastOfferAmount,
          lastMessage: (normalizeTimestamp(offerEntry.updatedAt) > normalizeTimestamp(existing.updatedAt)) ? offerEntry.lastMessage : (existing.lastMessage || offerEntry.lastMessage)
        });
      } else {
        merged.set(chatId, offerEntry);
      }
    }

    const arr = Array.from(merged.values()).sort((a, b) => {
      const aTs = normalizeTimestamp(a.updatedAt);
      const bTs = normalizeTimestamp(b.updatedAt);
      return (bTs || 0) - (aTs || 0);
    });

    try { cb(arr); } catch (_) {}
  };

  let unsubChats = null;
  let unsubOffers = null;

  try {
    unsubChats = chatService.listenChatsForUser(uid, (chatRows) => {
      chatsMap.clear();
      for (const r of (chatRows || [])) chatsMap.set(r.id, r);
      emit();
    });
  } catch (err) {
    // silent
  errorDev("Operation failed:", err);
  }

  try {
    unsubOffers = offersService.listenOffersForUser(uid, (offers) => {
      offersMap.clear();
      for (const o of (offers || [])) offersMap.set(o.id, o);
      emit();
    });
  } catch (err) {
    // silent
  errorDev("Operation failed:", err);
  }

  return () => {
    try { unsubChats && unsubChats(); } catch (e) {}
    try { unsubOffers && unsubOffers(); } catch (e) {}
  };
};

// Re-export for compatibility
export const findOrCreateChatWithContext = chatService.findOrCreateChatWithContext;